import { useEffect, useMemo, useCallback, useState } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Contact } from '../../../components/address-book';
import { handleAddressSubmitOneToMany } from '../../../components/address-book/utils/handle-address-submit-one-to-many';
import { useAnalyticsContext } from '../../../context/analytics-context';
import { useInitializationDataContext } from '../../../context/data-context/data-context';
import {
  AddressForm,
  AddressTypeCode,
  SaveAddressesResponse,
  SaveAddressResponse,
} from '../../../global-types/addresses';
import { EnvelopeType } from '../../../global-types/envelope';
import { ErrorResponse } from '../../../global-types/services';
import { useFeatureFlags } from '../../../hooks';
import useAddressSelection from '../../../hooks/use-address-selection';
import { useIsOneToMany } from '../../../hooks/use-is-one-to-many';
import { useSystemErrorHandling } from '../../../hooks/use-system-error-handling';
import { getAddressErrorType } from '../../../utils/address/get-address-error';
import { useEnvelopeSelection } from '../../select-envelope/hooks/use-envelope-selection';
import { handleErrorValidation } from '../utils/handle-error-validation';
import { mapSavedAddressToContact } from '../utils/map-saved-address-to-contact';
import { trackSubmittedNameOnEnvelope } from '../utils/track-submitted-name-on-envelope';
import { AddAddressToValidate } from './use-address-confirmation-dialog';
import { useNavigationBack } from './use-navigation-back';
import { useNavigationForward } from './use-navigation-forward';
import { useUpdateOneToManyAddresses } from './use-update-one-to-many-addresses';
import { didAddressChange } from './util/did-address-change';
import { handleSaveQuickAddressFromAddressForm } from './util/handle-save-quick-address-from-address-form';

export type UseOneToManyNavigationProps = {
  isFormValid: boolean;
  isAddressLoading: boolean;
  isAddressFormValid: boolean;
  handlePrimaryActionButton: () => void;
  handleSecondaryActionButton: () => void;
  addressFormValues: UseFormReturn<AddressForm>;
  isGetAddressesLoading: boolean;
  setIsRecipientAddressesDialogOpen: (isOpen: boolean) => void; // Add this
  // address validation
  addAddressToValidate: AddAddressToValidate;
  openAddressConfirmationDialog: () => void;
  closeAddressConfirmationDialog: () => void;
};

// This hook handles the navigation logic for 1:M cards
export const useOneToManyNavigation = ({
  isFormValid,
  isAddressLoading,
  isAddressFormValid,
  handlePrimaryActionButton,
  handleSecondaryActionButton,
  addressFormValues,
  isGetAddressesLoading,
  setIsRecipientAddressesDialogOpen,
  // address validation
  addAddressToValidate,
  openAddressConfirmationDialog,
  closeAddressConfirmationDialog,
}: UseOneToManyNavigationProps) => {
  // hooks
  const { t } = useTranslation();
  const { onSystemError } = useSystemErrorHandling();
  const { updateSenderAddress, updateRecipientAddress } = useUpdateOneToManyAddresses({
    addAddressToValidate,
    openAddressConfirmationDialog,
    closeAddressConfirmationDialog,
    setIsRecipientAddressesDialogOpen,
  });
  const { trackClickLeaveRemainingBlank, trackManuallyAddRecipientQuickAddress, trackNameOnEnvelopeUsage } =
    useAnalyticsContext();
  const {
    handleSetSender,
    handleSetRecipientContact,
    handleSetIsRecipientAddressStatus,
    handleDecrease,
    maxCount,
    count,
    sender,
    contacts,
    isInitialAddress,
    isRecipientAddress,
  } = useAddressSelection();
  const { selectedEnvelope } = useEnvelopeSelection();
  const isOneToMany = useIsOneToMany();
  const { IS_ADDRESS_VERIFICATION_ENHANCED_MATCH_ENABLED, IS_MULT_ADDRESS_ENABLED, IS_ADDRESS_DISPLAY_NAME_ENABLED } =
    useFeatureFlags();

  const isReturnEnvelope = selectedEnvelope === EnvelopeType.Return;

  const [isSubmittingAddress, setIsSubmittingAddress] = useState(false);

  const {
    initializedDataState: { data: initializedData, isUK },
  } = useInitializationDataContext();

  const {
    formState: { isValid, isDirty: isTouched, errors },
    reset: resetFormAddresses,
    getValues: handleGetFormValues,
    setValue: handleSetFormValue,
    setFocus: handleSetFocus,
  } = addressFormValues;

  // resets the address form values
  // to prepare for the next address input
  const resetForm: () => void = useCallback(() => {
    resetFormAddresses({
      address_id: '',
      first_name: '',
      last_name: '',
      display_name: '',
      address_line_1: '',
      address_line_2: '',
      company_name: '',
      city: '',
      state_code: '',
      zip: '',
      country_code: isUK ? 'UK' : 'USA',
      skip_usps_validation: false, // todo: this may need to be true depending on the current action save/resubmit/update
      isQuickAddress: !!handleGetFormValues('isQuickAddress'),
      is_verified: false,
      envelope: selectedEnvelope || null,
      address_type_code: isRecipientAddress ? AddressTypeCode.RECIPIENT : AddressTypeCode.SENDER,
    });
  }, [handleGetFormValues, isRecipientAddress, isUK, resetFormAddresses, selectedEnvelope]);

  const { contactAtIndex, assignForm } = useNavigationBack();
  const { assignForm: assignFormForward } = useNavigationForward();

  // secondary button props
  const onHandleGoPreviousAddress = () => {
    const contact = contactAtIndex(contacts, count - 2);
    const contactToAssign = !contact ? sender : contact;
    assignForm(contactToAssign, handleSetFormValue, handleSetFocus, resetFormAddresses);

    if (count === 1) {
      handleSetIsRecipientAddressStatus(false);
      return;
    }
    handleDecrease();
  };
  const onHandleGoReturnAddress = () => {
    isRecipientAddress
      ? onHandleGoPreviousAddress()
      : assignForm(sender, handleSetFormValue, handleSetFocus, resetFormAddresses);
  };
  const backText =
    selectedEnvelope === EnvelopeType.ReturnRecipient && isRecipientAddress
      ? t('addressForm.returnAddress')
      : t('addressForm.back');

  const shouldReturnToEdit = useMemo(() => count <= 1 && !isRecipientAddress, [count, isRecipientAddress]);

  const secondaryActionButton = {
    onClick: shouldReturnToEdit ? handleSecondaryActionButton : onHandleGoReturnAddress,
    text: count > 1 ? t('addressForm.previous') : backText,
    testId: 'address-form-return-address-button',
  };

  // primary button props

  // set the form values when the user is updating an address
  useEffect(() => {
    if (!isOneToMany) {
      return;
    }

    const addressForm: AddressForm = handleGetFormValues();
    const addressFormIsEmpty =
      !addressForm.address_hash_id || !addressForm.address_id || !addressForm.first_name || !addressForm.last_name;

    // when an address is already saved, we need to assign the form values to the correct address
    if (!isInitialAddress && !isRecipientAddress && addressFormIsEmpty) {
      // handle setting the form values for the sender address
      if (sender) {
        assignFormForward({
          handleSetFormValue,
          handleSetFocus,
          contact: sender,
          addressTypeCode: AddressTypeCode.SENDER,
          selectedEnvelope,
        });
      }
    } else if (!isInitialAddress && isRecipientAddress && addressFormIsEmpty) {
      // handle setting the form values for the recipient address
      const recipient: Contact | undefined = contacts[count - 1];
      if (recipient) {
        assignFormForward({
          handleSetFormValue,
          handleSetFocus,
          contact: recipient,
          addressTypeCode: AddressTypeCode.RECIPIENT,
          selectedEnvelope,
        });
      }
    }
  }, [
    contacts,
    count,
    handleGetFormValues,
    handleSetFormValue,
    handleSetFocus,
    isInitialAddress,
    isRecipientAddress,
    selectedEnvelope,
    sender,
    assignFormForward,
    isOneToMany,
    IS_MULT_ADDRESS_ENABLED,
  ]);

  // onAddAddress is called when the user clicks the primary button and the form is valid
  // it handles the address submission for 1:M cards for both sender and recipient addresses
  // and updates the address when the address is already saved
  // If the user resubmits the address, it will skip the validation
  const onAddAddress = async (isResubmit = false) => {
    const addressForm: AddressForm = handleGetFormValues();

    // this means the user is trying to update a sender address to a 1:M card
    if (addressForm.address_type_code === AddressTypeCode.SENDER && addressForm && addressForm.address_id) {
      const shouldSkipValidation =
        (addressForm.skip_usps_validation && !didAddressChange(addressForm, sender)) || isResubmit;
      addressForm.skip_usps_validation = shouldSkipValidation;
      setIsSubmittingAddress(true);
      try {
        await updateSenderAddress(addressForm, resetForm);
        setIsSubmittingAddress(false);
      } catch (error) {
        setIsSubmittingAddress(false);
      }
      return;
    }

    // this means the user is trying to update a recipient address to a 1:M card
    if (addressForm.address_type_code === AddressTypeCode.RECIPIENT && addressForm && addressForm.address_id) {
      const formContact = contacts.find((contact) => contact.contact_id === addressForm.address_id);
      const shouldSkipValidation =
        (addressForm.skip_usps_validation && !didAddressChange(addressForm, formContact)) || isResubmit;
      addressForm.skip_usps_validation = shouldSkipValidation;
      setIsSubmittingAddress(true);
      try {
        await updateRecipientAddress(addressForm, resetForm);
        setIsSubmittingAddress(false);

        // open the recipient addresses dialog
        // when the user has added all the addresses
        if (count === maxCount) {
          setIsRecipientAddressesDialogOpen(true);
          return;
        }
      } catch (error) {
        setIsSubmittingAddress(false);
      }
      return;
    }

    addressForm.skip_usps_validation = !!(isResubmit || addressForm.skip_usps_validation);

    // handling 1:M card when user is adding addresses
    setIsSubmittingAddress(true);

    try {
      const savedAddressResponse = await handleAddressSubmitOneToMany(
        initializedData?.project_id,
        addressForm,
        IS_ADDRESS_VERIFICATION_ENHANCED_MATCH_ENABLED,
        isRecipientAddress ? AddressTypeCode.RECIPIENT : AddressTypeCode.SENDER,
      );

      if (isSaveAddressResponse(savedAddressResponse)) {
        trackSubmittedNameOnEnvelope(addressForm, IS_ADDRESS_DISPLAY_NAME_ENABLED, trackNameOnEnvelopeUsage);
      }

      // Save the quick address if the address is a quick address
      await handleSaveQuickAddressFromAddressForm(addressForm);

      if (!isRecipientAddress) {
        // saving the sender address
        if (isSaveAddressResponse(savedAddressResponse)) {
          const contact = mapSavedAddressToContact(savedAddressResponse, initializedData?.account_id || '');
          contact && handleSetSender({ sender: contact, enableRecipientAddresses: true });
        }

        resetForm();

        // if address was submitted successfully after validation
        // close the confirmation dialog
        closeAddressConfirmationDialog();
        return;
      }

      // by default we assume the user is adding a recipient-address
      if (isSaveAddressResponse(savedAddressResponse)) {
        // map the saved address to a contact
        const contact = mapSavedAddressToContact(savedAddressResponse, initializedData?.account_id || '');
        // save the contact to the state if it's not null and increase the count
        contact && handleSetRecipientContact(contact);

        if (addressForm.isQuickAddress && addressForm.address_type_code === AddressTypeCode.RECIPIENT) {
          trackManuallyAddRecipientQuickAddress();
        }
      }

      if (count === maxCount) {
        setIsRecipientAddressesDialogOpen(true);
        return;
      }

      resetForm();

      // if address was submitted successfully after validation
      // close the confirmation dialog
      closeAddressConfirmationDialog();
    } catch (error) {
      // check if any of the errors are address errors
      const errorResponse = error as ErrorResponse;
      const allErrors = errorResponse.data.errors.flatMap((err) => getAddressErrorType(err) ?? []);

      // if there are any address errors, set the address to validate
      if (errorResponse.status === 422 && allErrors.length > 0) {
        handleErrorValidation(allErrors, addAddressToValidate, addressForm, isRecipientAddress);

        openAddressConfirmationDialog();
        return;
      }

      // if the error is not an address error, throw a system error
      onSystemError(errorResponse);
    } finally {
      setIsSubmittingAddress(false);
    }
  };

  const onResubmitAddress = async () => {
    // Maybe add analytics tracking here that the user clicked the skip address validation button
    closeAddressConfirmationDialog();
    await onAddAddress(true);
  };

  const onHandleLeaveRemainingBlank = () => {
    // Open the Recipient Address Dialog instead of setting the count
    setIsRecipientAddressesDialogOpen(true);
    trackClickLeaveRemainingBlank();
  };

  const shouldSave = useMemo(() => isTouched || isValid, [isTouched, isValid]);
  const onHandleNextButtonWithDirtyText = shouldSave ? () => onAddAddress() : onHandleLeaveRemainingBlank;
  // trigger the existing functionality when the envelope is "return", to prevent handling it as 1:M for return-recipient envelopes
  const onClick = isReturnEnvelope ? handlePrimaryActionButton : onHandleNextButtonWithDirtyText;

  // next label text
  const recipientAddressText = shouldSave
    ? t('addressForm.nextButton.addAddress')
    : t('addressForm.nextButton.leaveBlanks');
  // validate the text field when the envelope is "return"
  const isRnRAndRecipient = selectedEnvelope === EnvelopeType.ReturnRecipient && isRecipientAddress;
  const returnAddressText = isRnRAndRecipient ? t('addressForm.preview') : t('addressForm.next');
  const rnrLabel = isRecipientAddress ? recipientAddressText : returnAddressText;
  const primaryActionText = selectedEnvelope === EnvelopeType.Return ? t('addressForm.next') : rnrLabel;
  const hasDisplayNameError = IS_ADDRESS_DISPLAY_NAME_ENABLED && Boolean(errors?.display_name);

  const primaryActionButton = {
    onClick,
    text: primaryActionText,
    testId: 'address-form-continue-submit',
    isValid: isValid && isFormValid && isAddressFormValid,
    isLoading: isAddressLoading || isSubmittingAddress,
    isDisabled:
      hasDisplayNameError ||
      (!isRecipientAddress
        ? !isFormValid || isSubmittingAddress
        : (isTouched && !isFormValid) || isGetAddressesLoading || isAddressLoading || isSubmittingAddress),
    isTouched,
  };

  return {
    oneToManyProps: {
      secondaryActionButton,
      primaryActionButton,
    },
    onResubmitAddress,
  };
};

const isSaveAddressResponse = (
  response: SaveAddressesResponse | SaveAddressResponse,
): response is SaveAddressResponse => {
  return response.data != null && typeof response.data === 'object' && 'address_id' in response.data;
};
